import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { JdcManagerService } from '../jdc-manager.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';

@Component({
    selector: 'gtw-jdc-save-activity-modal',
    templateUrl: './jdc-save-activity-modal.component.html',
    styleUrls: ['./jdc-save-activity-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class JdcSaveActivityModalComponent implements OnChanges, OnDestroy {
  @Input() mode: 'new' | 'edit' = 'new';
  @Input() activityData: any;
  @Input() channelList: any[] = [];
  @Input() taskList: any[] = [];
  @Input() jcdKey: any;
  @Input() clientKey: any;
  @Input() selectedChannel: any;
  @Input() globalParams: any;
  @Input() selectedTask: any;
  @Input() client: any;

  @Output() saved = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  activityModalLoading = false;
  activityCrudLoading = false;
  activityMinimise = false;
  taxTypeDetails: any[] = [];
  taxTypeDetails2: any[] = [];
  selectedTaxTypes: any[] = [];
  activityRoles: any[] = [];
  activityForm: any = {
    activity_key: null, client: null, channel: null, task: '',
    screen_name: '', edit_level: 'O', lock_level: 'A', priority: 1,
    start_date: '', end_date: '', jcdTaRoleKeysToBeDeleted: ''
  };
  maxAllowedRoles = 20;
  addedTags: any[] = [];
  searchTagText = '';
  activityDescription = '';

  activityFormSubmitted = false;
  fieldErrors = {
    channel: false,
    task: false,
    screen_name: false,
    start_date: false,
    end_date: false,
    taxtype: false,
    roles: false,
  };
  roleFieldErrors: Array<{ role_name: boolean; role_desc: boolean; access_level: boolean }> = [];

  showTemplateModal = false;

  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = { title: '', message: '' };
  private confirmCallback: (() => void) | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private jdcManagerService: JdcManagerService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['mode'] && !changes['activityData']) {
      return;
    }
    this.activityMinimise = false;
    if (this.mode === 'edit' && this.activityData) {
      this.loadEditActivity();
    } else if (this.mode === 'new') {
      this.initNewActivity();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.closed.emit();
  }

  private initNewActivity(): void {
    this.activityForm = {
      activity_key: null, client: this.client, channel: this.selectedChannel || null,
      task: this.selectedTask?.task_key || '', screen_name: '', edit_level: 'O', lock_level: 'A',
      priority: 1, start_date: '', end_date: '', jcdTaRoleKeysToBeDeleted: ''
    };
    this.activityRoles = [{ role_name: '', role_desc: '', access_level: 1 }];
    this.selectedTaxTypes = [];
    this.taxTypeDetails2 = [];
    this.addedTags = [];
    this.searchTagText = '';
    this.activityDescription = '';
    this.resetValidationState();
    this.loadTaxTypesForModal();
  }

  private loadEditActivity(): void {
    this.activityModalLoading = true;
    this.activityRoles = [];
    this.selectedTaxTypes = [];
    this.taxTypeDetails2 = [];
    this.addedTags = [];
    this.searchTagText = '';
    this.activityDescription = '';
    this.jdcManagerService.getActivity(this.activityData?.activity_key, this.clientKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const row = res?.jcdActivity ?? res?.data?.jcdActivity ?? this.activityData;
          this.activityForm = {
            activity_key: row?.activity_key,
            client: this.client,
            channel: this.channelList.find((c) => c.channel_key === row?.channel?.channel_key || c.channel_key === row?.activity_channel) || this.selectedChannel || null,
            task: row?.activity_task || '',
            screen_name: row?.activity_name || '',
            edit_level: row?.activity_edit_level || 'O',
            lock_level: row?.activity_lock_level || 'A',
            priority: Number(row?.activity_priority || 1),
            start_date: this.toDateInputValue(row?.start_date || new Date()),
            end_date: this.toDateInputValue(row?.end_date || new Date('12/31/9999')),
            jcdTaRoleKeysToBeDeleted: ''
          };
          this.activityRoles = (row?.roles || []).map((r: any) => ({
            jcd_ta_role_key: r.jcd_ta_role_key, role_name: r.role_name,
            role_desc: r.role_desc, access_level: Number(r.access_level || 1)
          }));
          this.selectedTaxTypes = (row?.taxtypeList || []).map((t: any) => ({ id: t.id, label: t.label }));
          this.loadTaxTypesForModal();
          this.activityModalLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.activityModalLoading = false;
          this.gtwSnackbarService.openSnackBar('Problem loading activity.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      });
  }

  addActivityRole(): void {
    if (this.activityRoles.length >= this.maxAllowedRoles) return;
    this.activityRoles.unshift({ role_name: '', role_desc: '', access_level: 1 });
    if (this.activityFormSubmitted) {
      this.fieldErrors.roles = false;
      this.cdr.markForCheck();
    }
  }

  removeActivityRole(index: number): void {
    const role = this.activityRoles[index];
    if (role?.jcd_ta_role_key) {
      const current = this.activityForm.jcdTaRoleKeysToBeDeleted || '';
      this.activityForm.jcdTaRoleKeysToBeDeleted = current ? `${current},${role.jcd_ta_role_key}` : `${role.jcd_ta_role_key}`;
    }
    this.activityRoles.splice(index, 1);
  }

  addTag(text: string): void {
    if (!text?.trim()) return;
    this.addedTags.push({ name: text.trim() });
    this.searchTagText = '';
  }

  deleteTag(index: number): void {
    this.addedTags.splice(index, 1);
  }

  clearActivityForm(): void {
    const savedChannel = this.activityForm.channel;
    const savedClient = this.activityForm.client;
    const deletedKeys = this.activityForm.jcdTaRoleKeysToBeDeleted || '';
    const rolesKeys = this.activityRoles
      .filter((r: any) => r.jcd_ta_role_key)
      .map((r: any) => r.jcd_ta_role_key)
      .join(',');
    const allDeleted = [deletedKeys, rolesKeys].filter(Boolean).join(',');
    this.activityForm = {
      activity_key: this.activityForm.activity_key,
      client: savedClient, channel: savedChannel, task: '',
      screen_name: '', edit_level: 'O', lock_level: 'A', priority: 1,
      start_date: '', end_date: '', jcdTaRoleKeysToBeDeleted: allDeleted
    };
    this.activityRoles = [];
    this.selectedTaxTypes = [];
    this.taxTypeDetails2 = this.taxTypeDetails.map((item: any) => ({
      id: item.id, label: item.label, selected: false
    }));
    this.addedTags = [];
    this.searchTagText = '';
    this.activityDescription = '';
    this.resetValidationState();
    this.cdr.detectChanges();
  }

  askRemoveActivity(): void {
    this.confirmModalConfig = {
      title: 'Remove Activity',
      message: 'Are you sure you want to remove this activity?',
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default',
      showCancelButton: true,
      showConfirmButton: true,
    };
    this.confirmCallback = () => {
      this.removeActivityFromModal();
    };
    this.showConfirmModal = true;
    this.cdr.detectChanges();
  }

  executeConfirm(): void {
    this.showConfirmModal = false;
    if (this.confirmCallback) { this.confirmCallback(); }
    this.confirmCallback = null;
    this.cdr.detectChanges();
  }

  cancelConfirm(): void {
    this.showConfirmModal = false;
    this.confirmCallback = null;
    this.cdr.detectChanges();
  }

  removeActivityFromModal(): void {
    if (!this.activityForm?.activity_key) return;
    this.activityCrudLoading = true;
    this.jdcManagerService.deleteActivity(this.activityForm.activity_key, this.jcdKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.activityCrudLoading = false;
          const result = res?.result ?? res?.data?.result ?? {};
          if (Number(result?.value || 0) > 0) {
            this.gtwSnackbarService.openSnackBar('Activity removed successfully.', 'gtw-snackbar--green');
            this.saved.emit();
          } else {
            this.gtwSnackbarService.openSnackBar(result?.message || 'Problem removing activity.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.activityCrudLoading = false;
          this.gtwSnackbarService.openSnackBar('Problem removing activity.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      });
  }

  onTaxTypeSelectionChange(typ: any, checked: boolean): void {
    if (checked) {
      if (!this.selectedTaxTypes.some((x: any) => x.id === typ.id)) {
        this.selectedTaxTypes.push({ id: typ.id, label: typ.label });
      }
    } else {
      this.selectedTaxTypes = this.selectedTaxTypes.filter((x: any) => x.id !== typ.id);
    }
    if (this.activityFormSubmitted && this.selectedTaxTypes.length) {
      this.fieldErrors.taxtype = false;
      this.cdr.markForCheck();
    }
  }

  onActivityFieldChange(
    field: 'channel' | 'task' | 'screen_name' | 'start_date' | 'end_date',
    value: unknown,
  ): void {
    if (!this.activityFormSubmitted) {
      return;
    }
    switch (field) {
      case 'channel':
        this.fieldErrors.channel = this.isMissing(value);
        break;
      case 'task':
        this.fieldErrors.task = this.isMissing(value);
        break;
      case 'screen_name':
        this.fieldErrors.screen_name = !String(value ?? '').trim();
        break;
      case 'start_date':
        this.fieldErrors.start_date = !value;
        break;
      case 'end_date':
        this.fieldErrors.end_date = !value;
        break;
    }
    this.cdr.markForCheck();
  }

  onRoleFieldChange(
    index: number,
    field: 'role_name' | 'role_desc' | 'access_level',
    value: unknown,
  ): void {
    if (!this.activityFormSubmitted) {
      return;
    }
    if (this.activityRoles.length) {
      this.fieldErrors.roles = false;
    }
    if (!this.roleFieldErrors[index]) {
      return;
    }
    switch (field) {
      case 'role_name':
        this.roleFieldErrors[index].role_name = !String(value ?? '').trim();
        break;
      case 'role_desc':
        this.roleFieldErrors[index].role_desc = !String(value ?? '').trim();
        break;
      case 'access_level':
        this.roleFieldErrors[index].access_level = this.isMissing(value);
        break;
    }
    this.cdr.markForCheck();
  }

  private loadTaxTypesForModal(): void {
    this.jdcManagerService.getTaxTypeDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.taxTypeDetails = res?.data || [];
          this.taxTypeDetails2 = this.taxTypeDetails.map((item: any) => ({
            id: item.id, label: item.label,
            selected: this.selectedTaxTypes.some((x: any) => x.id === item.id)
          }));
          this.cdr.detectChanges();
        },
        error: () => { this.taxTypeDetails = []; this.taxTypeDetails2 = []; }
      });
  }

  saveActivityModal(): void {
    if (!this.validateActivityForm()) {
      this.cdr.detectChanges();
      return;
    }
    const startDate = new Date(this.activityForm.start_date);
    const endDate = new Date(this.activityForm.end_date);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      this.gtwSnackbarService.openSnackBar('Invalid Date.', 'gtw-snackbar--red');
      return;
    }
    if (startDate > endDate) {
      this.gtwSnackbarService.openSnackBar('End date cannot be before Start date.', 'gtw-snackbar--red');
      return;
    }
    const channelKey = this.activityForm.channel?.channel_key || this.activityForm.channel;
    const filterParams = {
      client: { value: this.clientKey },
      jurisdiction: { value: 1 },
      domain: { value: 9 },
      channel: { value: channelKey }
    };
    this.activityCrudLoading = true;
    this.cdr.detectChanges();
    this.jdcManagerService.getJCDKey(filterParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (jcdRes) => {
          const freshJcdKey = Number(jcdRes?.data?.jcdKey ?? jcdRes?.jcdKey ?? 0);
          if (!freshJcdKey) {
            this.activityCrudLoading = false;
            this.gtwSnackbarService.openSnackBar('Please select valid JCD combination!', 'gtw-snackbar--red');
            this.cdr.detectChanges();
            return;
          }
          const payload = {
            jcd_key: freshJcdKey, activity_key: this.activityForm.activity_key,
            client_key: this.clientKey, activity_jurisdiction: 1, activity_domain: 9,
            activity_channel: channelKey,
            activity_task: this.activityForm.task, activity_name: this.activityForm.screen_name,
            activity_edit_level: this.activityForm.edit_level || 'O',
            activity_lock_level: this.activityForm.lock_level || 'A',
            activity_priority: this.activityForm.priority || 1,
            start_date: this.toMmDdYyyy(this.activityForm.start_date),
            end_date: this.toMmDdYyyy(this.activityForm.end_date),
            taxtypeList: this.selectedTaxTypes.map((x: any) => ({ id: x.id, label: x.label })),
            roles: this.activityRoles.map((r: any) => ({
              jcd_ta_role_key: r.jcd_ta_role_key || '', role_name: r.role_name,
              role_desc: r.role_desc, access_level: Number(r.access_level || 1)
            })),
            jcdTaRoleKeysToBeDeleted: (this.activityForm.jcdTaRoleKeysToBeDeleted || '').replace(/^,/, '')
          };
          this.jdcManagerService.saveActivity(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res) => {
                this.activityCrudLoading = false;
                const saveResult = res?.result ?? res?.data?.result ?? {};
                if (Number(saveResult?.value || 0) > 0) {
                  this.gtwSnackbarService.openSnackBar(`Activity ${payload.activity_name} saved successfully.`, 'gtw-snackbar--green');
                  this.saved.emit();
                } else {
                  this.gtwSnackbarService.openSnackBar(`Problem saving activity: ${saveResult?.message || ''}`, 'gtw-snackbar--red');
                }
                this.cdr.detectChanges();
              },
              error: () => {
                this.activityCrudLoading = false;
                this.gtwSnackbarService.openSnackBar('Problem saving activity.', 'gtw-snackbar--red');
                this.cdr.detectChanges();
              }
            });
        },
        error: () => {
          this.activityCrudLoading = false;
          this.gtwSnackbarService.openSnackBar('Error fetching JCD key. Cannot save activity.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      });
  }

  private resetValidationState(): void {
    this.activityFormSubmitted = false;
    this.fieldErrors = {
      channel: false,
      task: false,
      screen_name: false,
      start_date: false,
      end_date: false,
      taxtype: false,
      roles: false,
    };
    this.roleFieldErrors = [];
  }

  private isMissing(value: unknown): boolean {
    return value === undefined || value === null || value === '';
  }

  private validateActivityForm(): boolean {
    this.activityFormSubmitted = true;
    this.fieldErrors = {
      channel: false,
      task: false,
      screen_name: false,
      start_date: false,
      end_date: false,
      taxtype: false,
      roles: false,
    };
    this.roleFieldErrors = [];
    let valid = true;

    if (this.isMissing(this.activityForm?.channel)) {
      this.fieldErrors.channel = true;
      valid = false;
    }
    if (this.isMissing(this.activityForm?.task)) {
      this.fieldErrors.task = true;
      valid = false;
    }
    if (!this.activityForm?.screen_name?.trim()) {
      this.fieldErrors.screen_name = true;
      valid = false;
    }

    if (!this.activityRoles.length) {
      this.fieldErrors.roles = true;
      this.gtwSnackbarService.openSnackBar('Please enter at least one role to save.', 'gtw-snackbar--orange');
      valid = false;
    } else {
      this.roleFieldErrors = this.activityRoles.map((role) => ({
        role_name: !role.role_name?.trim(),
        role_desc: !role.role_desc?.trim(),
        access_level: this.isMissing(role.access_level),
      }));
      if (this.roleFieldErrors.some((e) => e.role_name || e.role_desc || e.access_level)) {
        valid = false;
      }
    }

    if (!this.selectedTaxTypes.length) {
      this.fieldErrors.taxtype = true;
      valid = false;
    }
    if (!this.activityForm.start_date) {
      this.fieldErrors.start_date = true;
      valid = false;
    }
    if (!this.activityForm.end_date) {
      this.fieldErrors.end_date = true;
      valid = false;
    }

    return valid;
  }

  openAssignTemplateModal(): void {
    this.showTemplateModal = true;
    this.cdr.detectChanges();
  }

  onTemplateSaved(): void {
    this.showTemplateModal = false;
    this.cdr.detectChanges();
  }

  onTemplateClosed(): void {
    this.showTemplateModal = false;
    this.cdr.detectChanges();
  }

  private toMmDdYyyy(value: any): string {
    const d = value ? new Date(value) : null;
    if (!d || Number.isNaN(d.getTime())) return '';
    const mm = `${d.getMonth() + 1}`.padStart(2, '0');
    const dd = `${d.getDate()}`.padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()}`;
  }

  private toDateInputValue(value: any): string {
    const d = value ? new Date(value) : null;
    if (!d || Number.isNaN(d.getTime())) return '';
    const mm = `${d.getMonth() + 1}`.padStart(2, '0');
    const dd = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }
}
